import IFrameContainerService from '../abstracts/containers/iFrameContainerService'

export default class YouTube extends IFrameContainerService {
  constructor(settings, CookieManager) {
    // Abstract constructor
    super(settings, CookieManager)

    // required parameters
    this.uri = 'https://policies.google.com/privacy'
    this.cookies = ['VISITOR_INFO1_LIVE', 'YSC', 'PREF', 'GEUP']

    // listen to content enabled / disabled events
    this.addYouTubeListeners()
  }

  /**
   * VIDEO THUMBNAILS
   */
  getBaseVideoUrl() {
    return 'https://www.youtube.com/watch?v='
  }

  getVideoIdRegex() {
    return /^.*(?:youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]{11})(.*)/
  }

  /**
   * ADDITIONAL PROCESSING FOR YOUTUBE VIDEOS
   */
  addYouTubeListeners() {
    document.addEventListener(`rsq.service.${this.identifier}.content.enabled`, (e) => {
      const enabledContentEl = e.detail.target
      const containerEl = e.detail.container
      const info = this.getEmbeddedContentElementInfo(enabledContentEl)

      // switch to youtube-nocookie.com if necessary
      this.ensureNoCookie(enabledContentEl, 'data-src')

      // don't set no-cookie on video element if it's lazy loaded (no-cookie link will be picked from data-lazy-src)
      if (!info.lazy) {
        this.ensureNoCookie(containerEl, this.embeddedSrcAttribute, enabledContentEl)
      }

      // set default query arguments for all YouTube videos
      this.setDefaultYouTubeQueryStringParameters(containerEl, enabledContentEl)
    })

    document.addEventListener(`rsq.service.${this.identifier}.content.disabled`, (e) => {
      // switch to youtube-nocookie.com if necessary
      this.ensureNoCookie(e.detail.target, 'data-src')
    })

    document.addEventListener(`rsq.service.${this.identifier}.content.container`, (e) => {
      // add base attributes to embedded container on trigger (used for modals etc.)
      const attr = {
        // based on default YouTube attributes when you embed a video
        allow: 'accelerometer; encrypted-media; gyroscope; picture-in-picture;',
        allowfullscreen: true
      }

      e.detail.container.setAttribute('data-attr', JSON.stringify(attr))
    })
  }

  getNoCookieBaseUrl() {
    return 'https://www.youtube-nocookie.com/embed/'
  }

  ensureNoCookie(element, srcAttrName, embeddedElement) {
    let srcAttr = element.getAttribute(srcAttrName)
    const info = this.getEmbeddedContentElementInfo(element)
    if (info && info.lazy) {
      srcAttr = info.src
    }

    if (srcAttr.indexOf('youtube-nocookie') === -1) {
      embeddedElement = embeddedElement || element
      const videoId = this.getVideoId(embeddedElement)
      if (videoId) {
        // replace initial YouTube domain by youtube-nocookie.com
        const initialBaseUrl = srcAttr.substring(0, srcAttr.indexOf(videoId))
        srcAttr = srcAttr.replace(initialBaseUrl, this.getNoCookieBaseUrl())

        // special handling for lazy loaded videos (WP Rocket)
        if (info && info.lazy) {
          this.updateEmbeddedContentElementLazySrc(embeddedElement, srcAttr)
        } else {
          element.setAttribute(srcAttrName, srcAttr)
        }
      }
    }

    return element
  }

  setDefaultYouTubeQueryStringParameters(containerEl, enabledContentEl) {
    const info = this.getEmbeddedContentElementInfo(enabledContentEl)
    let srcAttrValue = info.src

    /* set autoplay in the following cases:
    * - there's no clicked element and current element had autoplay in its initial source attribute
    * - current element is the clicked element, regardless of its initial autoplay state
    */
    const isClickedElement = this.isClickedElement(enabledContentEl)
    const hasAutoplayEnabled = this.hasEnabledYouTubeQueryStringParameter(srcAttrValue, 'autoplay')
    const setAutoplay = isClickedElement || !this.hasClickedElement() && hasAutoplayEnabled

    // feel free to add more parameters here
    const parameters = {
      autoplay: {
        set: setAutoplay,
        setAllow: true
      },
      modestbranding: true
    }

    for (const parameter in parameters) {
      if (Object.prototype.hasOwnProperty.call(parameters, parameter)) {
        // determine if this parameter should be set or not (based on above config)
        let set = false
        let setAllow = false
        switch (typeof parameters[parameter]) {
          case 'boolean':
            set = parameters[parameter]
            break
          case 'object':
            for (const key in parameters[parameter]) {
              if (Object.prototype.hasOwnProperty.call(parameters[parameter], key)) {
                const value = parameters[parameter][key]
                switch (key) {
                  case 'set':
                    set = value
                    break
                  case 'setAllow':
                    setAllow = value
                    break
                  default:
                    break
                }
              }
            }
            break
          default:
            break
        }

        // set or remove parameter from query string
        srcAttrValue = this.setYouTubeQueryStringParameter(containerEl, parameter, srcAttrValue, set, setAllow)
      }
    }

    if (info.lazy) {
      // for lazy loaded videos (WP Rocket): update lazy source instead of video source
      this.updateEmbeddedContentElementLazySrc(enabledContentEl, srcAttrValue)
      containerEl.setAttribute(this.getLazyLoadSrcAttribute(), srcAttrValue)
    } else {
      // reflect source attribute changes once for all (because each change cause video to reload)
      containerEl.setAttribute(this.embeddedSrcAttribute, srcAttrValue)
    }
  }

  setYouTubeQueryStringParameter(element, paramName, srcAttrValue, set = true, setAllow = false) {
    if (set) {
      if (srcAttrValue.indexOf(paramName) === -1) {
        // check if video already has query string parameters and use the appropriate prefix for new parameter
        const parameterPrefix = srcAttrValue.indexOf('?') !== -1 ? '&' : '?'
        srcAttrValue = `${srcAttrValue}${parameterPrefix}${paramName}=1`
      }

      if (setAllow) {
        // check for an existing "allow" attribute and ensure it's properly formatted
        let allowAttr = ''
        if (element.hasAttribute('allow')) {
          allowAttr = element.getAttribute('allow')
          if (!allowAttr.endsWith(';')) {
            allowAttr += ';'
          }
        }

        // add parameter to "allow" attribute
        if (allowAttr.indexOf(paramName) === -1) {
          allowAttr += `${paramName};`
          element.setAttribute('allow', allowAttr)
        }
      }
    } else if (this.hasEnabledYouTubeQueryStringParameter(srcAttrValue, paramName)) {
      // remove parameter from query string
      const regex = new RegExp(`([?|&])${paramName}=1`, 'g')
      srcAttrValue = srcAttrValue.replace(regex, '')
    }

    return srcAttrValue
  }

  hasEnabledYouTubeQueryStringParameter(srcAttrValue, paramName) {
    return srcAttrValue.indexOf(`${paramName}=1`) !== -1
  }
}
